import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { io, type Socket } from 'socket.io-client'
import { EVENTOS } from '@crm/shared'

/**
 * Uma conexão de tempo real só, dividida por quem precisa.
 *
 * Era aberta pelo bate-papo e fechava ao sair dele. Com o som das mensagens
 * (ADR 0040), o resto do sistema também precisa ouvir, e a pessoa está nos
 * Leads quando o cliente escreve. Duas conexões por aba dobrariam o trabalho
 * do servidor à toa: esta fica aberta enquanto alguém a usa, e fecha quando
 * o último solta (na saída do sistema, por exemplo).
 */
let compartilhada: Socket | null = null
let usos = 0

export function pegarSocket(): Socket {
  if (!compartilhada) compartilhada = io({ path: '/socket.io', withCredentials: true })
  usos++
  return compartilhada
}

export function soltarSocket(): void {
  usos = Math.max(0, usos - 1)
  if (usos === 0 && compartilhada) {
    compartilhada.close()
    compartilhada = null
  }
}

/**
 * Conexão de tempo real do bate-papo.
 *
 * O evento não carrega a conversa inteira: ele avisa que mudou e o TanStack
 * Query recarrega. Assim a máscara de telefone e o recorte de permissão
 * continuam sendo decididos pelo servidor, na resposta HTTP, e não por um
 * payload solto vindo do socket.
 */
export function useTempoReal(conexaoId: string | null) {
  const cliente = useQueryClient()

  useEffect(() => {
    const socket = pegarSocket()

    const recarregarConversas = () => {
      void cliente.invalidateQueries({ queryKey: ['conversas'] })
    }
    const recarregarMensagens = (dados: unknown) => {
      const payload = dados as { chamadoId?: string }
      if (payload?.chamadoId) {
        void cliente.invalidateQueries({ queryKey: ['mensagens', payload.chamadoId] })
        // O dono, o apoio e o retorno da conversa aberta, que o cabeçalho e a caixa usam.
        void cliente.invalidateQueries({ queryKey: ['chamado', payload.chamadoId] })
      }
      recarregarConversas()
    }
    const recarregarConexoes = () => {
      void cliente.invalidateQueries({ queryKey: ['conexoes'] })
    }
    const recarregarQrcode = () => {
      void cliente.invalidateQueries({ queryKey: ['qrcode'] })
    }

    socket.on(EVENTOS.mensagemNova, recarregarMensagens)
    socket.on(EVENTOS.mensagemAtualizada, recarregarMensagens)
    /*
      Conversa mudou: as mensagens dela recarregam junto (mapa de uso,
      08/10/2026). As linhas do sistema, como "transferiu", "encerrou" e
      "marcou um retorno", são gravadas sem o aviso de mensagem nova, e só
      apareciam na conversa aberta depois de recarregar a página.
    */
    socket.on(EVENTOS.chamadoAtualizado, recarregarMensagens)
    socket.on(EVENTOS.chamadoTransferido, recarregarMensagens)
    socket.on(EVENTOS.conexaoStatus, recarregarConexoes)
    socket.on(EVENTOS.conexaoQrcode, recarregarQrcode)

    return () => {
      // Tira só os ouvintes do bate-papo: o socket continua para o som.
      socket.off(EVENTOS.mensagemNova, recarregarMensagens)
      socket.off(EVENTOS.mensagemAtualizada, recarregarMensagens)
      socket.off(EVENTOS.chamadoAtualizado, recarregarMensagens)
      socket.off(EVENTOS.chamadoTransferido, recarregarMensagens)
      socket.off(EVENTOS.conexaoStatus, recarregarConexoes)
      socket.off(EVENTOS.conexaoQrcode, recarregarQrcode)
      soltarSocket()
    }
  }, [cliente])

  useEffect(() => {
    if (!conexaoId) return
    const socket = pegarSocket()
    const entrar = () => socket.emit('entrar:conexao', conexaoId)
    entrar()
    // Reconectado (servidor reiniciou, rede voltou), a sala precisa ser pedida de novo.
    socket.on('connect', entrar)

    return () => {
      socket.off('connect', entrar)
      socket.emit('sair:conexao', conexaoId)
      soltarSocket()
    }
  }, [conexaoId])
}
